import React, { ReactElement, useCallback, useEffect, useRef, useState } from 'react';
import isEqual from 'lodash.isequal';
import { Barcode, BarcodePicker, configure, ScanSettings } from 'scandit-sdk';
import { TOAST_TYPE } from 'b2b-apps/shop/common/components/Toast';
import { showToast } from 'b2b-apps/shop/common/utils/showToast';
import { config } from 'b2b-apps/shop/config';
import { useTranslate } from 'b2b-apps/shop/i18n/hooks';
import { CODE_SCANNER_ICON, CODE_SCANNER_UI_CLASS_NAME } from '../';
import { EnhancedComponentProps, EnhancedComponentType, InjectedProps, WrappedComponentType } from './';

const withContainer = (Component: WrappedComponentType): EnhancedComponentType => {
  const WithContainer = (props: EnhancedComponentProps): ReactElement => {
    const t = useTranslate();
    const {
      onCameraAccessDenied,
      onReady,
      onScan,
      ...originalProps
    } = props;
    const codeScannerElement = useRef<HTMLDivElement | null>(null);
    const codeScanner = useRef<BarcodePicker | null>(null);
    const [isCodeScannerReady, setIsCodeScannerReady] = useState(false);

    const destroyCodeScanner = useCallback(
      (): void => {
        codeScanner.current?.destroy();
      },
      [],
    );

    const updateCodeScannerUI = useCallback(
      (): void => {
        const element = codeScannerElement.current;
        if (!element) {
          return;
        }

        const viewFinderElement = element
          .getElementsByClassName(CODE_SCANNER_UI_CLASS_NAME.viewfinder)?.[0];
        const cameraSwitchIconElement = element
          .getElementsByClassName(CODE_SCANNER_UI_CLASS_NAME.cameraSwitcherIcon)?.[0];
        const torchToggleIconElement = element
          .getElementsByClassName(CODE_SCANNER_UI_CLASS_NAME.torchToggleIcon)?.[0];

        viewFinderElement?.classList?.add(CODE_SCANNER_UI_CLASS_NAME.hidden);
        cameraSwitchIconElement?.setAttribute('src', CODE_SCANNER_ICON.cameraSwitcher);
        torchToggleIconElement?.setAttribute('src', CODE_SCANNER_ICON.torchToggle);
      },
      [],
    );

    const handleCodeScannerReady = useCallback(
      (codeScannerInstance: Record<string, any>): (() => void) => (): void => {
        codeScannerInstance
          .accessCamera()
          .then(() => {
            updateCodeScannerUI();
            codeScannerInstance.setVisible(true);
            setIsCodeScannerReady(true);
            onReady && onReady();
          })
          .catch(() => {
            showToast(
              TOAST_TYPE.warning,
              t('Code scanner requires camera access'),
              t('Refresh and allow camera access'),
            );
            onCameraAccessDenied && onCameraAccessDenied();
          });
      },
      [onCameraAccessDenied, onReady, updateCodeScannerUI],
    );

    const handleCodeScannerScan = useCallback(
      ({ barcodes }: Record<string, any>): void => onScan(barcodes[0].data),
      [onScan],
    );

    useEffect(
      (): (() => void) => {
        configure(config.scandit.licenseKey, {
          engineLocation: 'https://cdn.jsdelivr.net/npm/scandit-sdk@5.x/build/',
        }).then((): void => {
          destroyCodeScanner();

          if (codeScannerElement.current) {
            BarcodePicker.create(
              codeScannerElement.current,
              {
                ...originalProps,
                accessCamera: false,
                enableCameraSwitcher: true,
                enableTorchToggle: true,
                guiStyle: BarcodePicker.GuiStyle.VIEWFINDER,
                scanningPaused: false,
                scanSettings: new ScanSettings({
                  enabledSymbologies: [Barcode.Symbology.EAN8, Barcode.Symbology.EAN13],
                  searchArea: {
                    height: 0.1,
                    width: 0.8,
                    x: 0.1,
                    y: 0.45,
                  },
                }),
                videoFit: BarcodePicker.ObjectFit.COVER,
                visible: false,
              },
            ).then(
              (codeScannerInstance) => {
                codeScannerInstance
                  .on('ready', handleCodeScannerReady(codeScannerInstance))
                  .on('scan', handleCodeScannerScan);
                codeScanner.current = codeScannerInstance;
              },
            );
          }
        });

        return destroyCodeScanner;
      },
      [
        destroyCodeScanner,
        handleCodeScannerReady,
        handleCodeScannerScan,
        originalProps,
      ],
    );

    const injectedProps: InjectedProps = {
      codeScannerElement,
      isCodeScannerReady,
    };

    return <Component {...originalProps} {...injectedProps} />;
  };

  return React.memo(WithContainer, (prevProps, nextProps) => isEqual(prevProps, nextProps));
};

export { withContainer };
